import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ApiService from "../../service/ApiService";
import { apiPaths } from "../../service/apiPaths";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import { initializeCart, toggleAuthentication } from "../../redux/hejalSlice";
import OtpInput from "react-otp-input";

const VerifyOtp = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { email, algoName } = location.state || {};
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [loading,setLoading]=useState();

  const handleVerifyOtp = async (e) => {
    e.preventDefault();

    if (!otp || otp.length !== 6) {
      setError("Enter a valid 6-digit OTP");
      return;
    }

    try {
      const response = await ApiService({
        method: "POST",
        endpoint: apiPaths.verifyOTP,
        data: { email, otp },
      });

      if (response.errorCode) {
        toast.error(response.message || "Invalid OTP, try again!");
        setError("Invalid OTP");
      } else {
        dispatch(toggleAuthentication());
        if (response.user?.cart) {
          dispatch(initializeCart(response.user.cart));
        }

        setSuccessMsg("OTP verified successfully! Redirecting...");
        toast.success("OTP verified!");
        setTimeout(
          () =>
            navigate("/newpassword", {
              state: { email, otp, algoName },
            }),
          2000
        );
      }
    } catch (error) {
      console.error("OTP verification failed:", error);
      toast.error("Something went wrong, try again.");
    }
  };

  const handleReSendOtp = async () => {
    // Basic validation
    if (!email) {
      toast.error("We Can't Find Your Email, Please Go Back");
      return;
    }

    try {
      // API call for login
      const response = await ApiService({
        method: "POST",
        endpoint: apiPaths.ForgotPassword,
        data: { email },
      });

      dispatch(toggleAuthentication());
      if (response.user?.cart) {
        dispatch(initializeCart(response.user?.cart));
      }

      if (response.errorCode) {
        toast.error(response.message || "Try again!");
      } else {
        // Save the new algoName from the response if it exists
        if (response.data && response.data.algoName) {
          // Update local state with the new algoName
          // This will be passed to the next component
          setAlgoName(response.data.algoName);
        }
        toast.success("Resent OTP to your email");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Server error.");
    }
  };

  const [isDisabled, setIsDisabled] = useState(true);
  const [timer, setTimer] = useState(60); // 60 seconds countdown
  const [localAlgoName, setAlgoName] = useState(algoName); // Store algoName locally in case it changes on resend

  useEffect(() => {
    let interval;
    if (isDisabled) {
      interval = setInterval(() => {
        setTimer((prev) => {
          if (prev === 1) {
            clearInterval(interval);
            setIsDisabled(false);
            return 60; // Reset timer for next click
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isDisabled]);

  const handleResendClick = async () => {
    if (isDisabled) return; // Prevent clicking when already disabled
    await handleReSendOtp(); // Ensure OTP resend API call completes before disabling
    setIsDisabled(true); // Now disable button after API call
    setTimer(60); // Restart timer
  };

  return (
    <div className="w-full h-screen flex items-center justify-center bg-[#efefef]">
      <div className="otp-box bg-[#fff] lg:min-h-[300px] lg:min-w-[400px] min-h-[200px] min-w-[300px] flex flex-col items-center justify-between py-5 px-8 gap-4">
        <div className="flex flex-col items-center">
          <h1 className="font-titleFont decoration-[1px] text-center font-semibold text-[20px] mdl:text-[28px] mb-4">
            Verify OTP
          </h1>
          <p className="mb-8 text-[14px] text-[#565656]">
            We sent an OTP to your email:{" "}
            <span className="font-[500] text-[#000]">{email}</span>
          </p>

          <OtpInput
            value={otp}
            onChange={setOtp}
            numInputs={6}
            renderSeparator={<span></span>}
            renderInput={(props) => (
              <input
                {...props}
                className="otp-input border border-gray-300 text-center w-12 h-12 text-xl"
              />
            )}
            inputStyle={{
              width: "50px",
              height: "50px",
              margin: "0 10px",
              fontSize: "20px",
              textAlign: "center",
              border: "1px solid #ccc",
              borderRadius: "5px",
            }}
          />
          {error && <p className="text-red-500 mt-2">{error}</p>}
          {successMsg && <p className="text-green-500 mt-2">{successMsg}</p>}
          <p className="mt-4 text-[14px]">
            Didn't receive the OTP?{" "}
            <span
              className={`font-[600] text-[#000] ${isDisabled
                  ? "text-gray-500 cursor-not-allowed"
                  : "hover:text-[#ff0000] cursor-pointer"
                } duration-300`}
              onClick={!isDisabled ? handleResendClick : undefined}
            >
              {isDisabled ? `Resend in ${timer}s` : "Resend"}
            </span>
          </p>
        </div>

        <div className="w-full">
          <button
            onClick={handleVerifyOtp}
            type="submit"
            disabled={loading}
            className={`bg-secondColor hover:bg-black text-black-200 hover:text-white cursor-pointer w-full text-base font-medium h-10 rounded-md duration-300 flex items-center justify-center ${loading ? "opacity-70 cursor-not-allowed" : ""
              }`}
          >
            {loading ? (
              <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-middle motion-reduce:animate-[spin_1.5s_linear_infinite]"></span>
            ) : (
              "Submit OTP"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VerifyOtp;
